import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { packsLabel } from "@/lib/utils";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, ClipboardCheck, CheckCircle2, XCircle, Pencil,
  Clock, AlertTriangle, Copy, FileText, ShieldCheck, CalendarClock,
} from "lucide-react";

/* REACT_APP_BACKEND_URL is empty in this deployment, which makes every API path relative.
   The || "" guard stops an unset var from producing a literal "undefined/api/..." src. */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const PUBLIC_ORIGIN = "https://pawlinx.com";

/* Every colour pair here is light/dark explicit on purpose - the admin console is audited for
   WCAG contrast in BOTH themes, so a dark-only token (e.g. text-emerald-300 with no light
   counterpart) would fail the light-mode audit. */
const STATE_BADGE = {
  pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/25",
  approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/25",
  adjusted: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/25",
  rejected: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/25",
};
const STATE_LABEL = {
  pending: "Awaiting review",
  approved: "Approved",
  adjusted: "Quantity adjusted",
  rejected: "Rejected",
};

const TABS = [
  { key: "pending", label: "Awaiting review" },
  { key: "decided", label: "Decided" },
  { key: "all", label: "All" },
];

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const qtyInt = (v, dflt = 1) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : dflt;
};

/* Mirrors the backend helpers exactly. Pack sizes are stored as free text ("120 Tablets") and the
   technician picks a plain number from a dropdown, so keep the unit word and swap only the number. */
const packNum = (p) => {
  const n = parseInt(String(p || "").replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const packLabel = (size, template) => {
  const unit = String(template || "").replace(/^[0-9\s.,]+/, "").trim();
  return unit ? `${size} ${unit}` : String(size);
};

const fmtWhen = (iso) => {
  if (!iso) return "n/a";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const hoursUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return (d.getTime() - Date.now()) / 3600000;
};

/* "in 6h" / "4h overdue" - a technician needs the SLA at a glance, not a raw timestamp. */
const countdown = (iso) => {
  const h = hoursUntil(iso);
  if (h == null) return "n/a";
  const abs = Math.abs(h);
  const txt = abs >= 24 ? `${Math.floor(abs / 24)}d ${Math.round(abs % 24)}h` : abs >= 1 ? `${Math.round(abs)}h` : `${Math.max(1, Math.round(abs * 60))}m`;
  return h >= 0 ? `in ${txt}` : `${txt} overdue`;
};

const MAX_SUPPLY_DAYS = 90;   // house rule: never more than a three month supply in one order

/* Works out days of supply from the prescribed dose. The technician types the dose off the
   prescription; nothing is guessed, and nothing here blocks a decision. It is arithmetic on
   screen so the three month rule does not have to be done in anyone's head. */
function SupplyCalculator({ dosesPerDay, setDosesPerDay, unitsRequested, packUnits, unitLabel, onUseQty }) {
    const dpd = parseFloat(dosesPerDay);
    const valid = Number.isFinite(dpd) && dpd > 0;
    const days = valid && unitsRequested > 0 ? unitsRequested / dpd : 0;
    const over = valid && days > MAX_SUPPLY_DAYS + 0.001;
    const maxUnits = valid ? MAX_SUPPLY_DAYS * dpd : 0;
    const per = packUnits > 0 ? packUnits : 0;
    const packsDown = valid && per ? Math.floor(maxUnits / per) : 0;
    const packsUp = valid && per ? Math.ceil(maxUnits / per) : 0;
    const fmtDays = (d) => `${Math.floor(d)} days (about ${(d / 30).toFixed(1)} months)`;
    const line = "flex items-center justify-between gap-3 py-1";
    const lbl = "font-manrope text-xs text-paw-text-secondary dark:text-white/60";
    const val = "font-manrope text-xs font-semibold text-paw-text dark:text-white/90 text-right";
    return (
      <Card title="Three month supply check" icon={CalendarClock} testid="rx-supply">
        <p className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 mb-3 leading-relaxed">
          Read the dose off the prescription and type it here. We dispense at most a three month supply
          in one order. This works out the numbers, it does not decide anything. The quantity is still yours to set.
        </p>
        <label className="block font-manrope text-xs font-medium text-paw-text dark:text-white/80 mb-1" htmlFor="rx-dpd">
          Doses per day as prescribed
        </label>
        <input
          id="rx-dpd" type="number" min="0" step="0.5" value={dosesPerDay}
          onChange={(e) => setDosesPerDay(e.target.value)}
          placeholder="for example 2 puffs a day"
          className="w-full h-10 rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 px-3 font-manrope text-sm text-paw-text dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-paw-primary/40"
          data-testid="rx-doses-per-day"
        />
        {valid && unitsRequested > 0 && (
          <div className="mt-3 border-t border-paw-border dark:border-white/10 pt-2" data-testid="rx-supply-result">
            <div className={line}>
              <span className={lbl}>Customer asked for</span>
              <span className={val}>{unitsRequested} {unitLabel}</span>
            </div>
            <div className={line}>
              <span className={lbl}>That lasts</span>
              <span className={`${val} ${over ? "text-paw-error dark:text-red-300" : ""}`} data-testid="rx-supply-days">{fmtDays(days)}</span>
            </div>
            <div className={line}>
              <span className={lbl}>Three months at this dose</span>
              <span className={val}>{Math.round(maxUnits)} {unitLabel}</span>
            </div>
            {over ? (
              <div className="mt-2 flex items-start gap-2 rounded-lg bg-paw-error/10 border border-paw-error/25 px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-paw-error flex-shrink-0 mt-0.5" />
                <p className="font-manrope text-xs text-paw-error leading-relaxed" data-testid="rx-supply-over">
                  Over the three month maximum. {per > 0 ? (
                    <>Inside 90 days is {packsDown} pack{packsDown === 1 ? "" : "s"} ({packsDown * per} {unitLabel},{" "}
                    {fmtDays(packsDown * per / dpd)}). {packsUp !== packsDown && (
                      <>Rounding up to {packsUp} pack{packsUp === 1 ? "" : "s"} gives {packsUp * per} {unitLabel}, {fmtDays(packsUp * per / dpd)}.</>
                    )}</>
                  ) : <>Reduce to {Math.round(maxUnits)} {unitLabel} or fewer.</>}
                </p>
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-paw-success/10 border border-paw-success/25 px-3 py-2">
                <CheckCircle2 className="w-4 h-4 text-paw-success flex-shrink-0" />
                <p className="font-manrope text-xs text-paw-success" data-testid="rx-supply-ok">Inside the three month maximum.</p>
              </div>
            )}
            {over && per > 0 && packsDown >= 1 && (
              <button type="button" onClick={() => onUseQty(packsDown)}
                className="mt-2 w-full h-9 rounded-lg border border-paw-primary/40 text-paw-primary dark:text-paw-primary font-manrope text-xs font-semibold hover:bg-paw-primary/5 transition-colors"
                data-testid="rx-supply-use">
                Use {packsDown} pack{packsDown === 1 ? "" : "s"} in the adjust box
              </button>
            )}
          </div>
        )}
      </Card>
    );
}

function Row({ label, value, testid, mono }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-paw-border dark:border-white/5 last:border-0">
      <span className="font-manrope text-xs text-paw-text-muted dark:text-white/40 shrink-0">{label}</span>
      <span className={`font-manrope text-sm text-paw-text dark:text-white/85 text-right break-words ${mono ? "font-mono text-xs" : ""}`} data-testid={testid}>{value}</span>
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5" data-testid={testid}>
      <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-3 flex items-center gap-1.5">
        {Icon ? <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> : null} {title}
      </p>
      {children}
    </div>
  );
}

export default function AdminPrescriptionReview() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ pending_count: 0, sla_hours: 24, pay_window_hours: 72 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState(null); // null | approve | adjust | reject
  const [note, setNote] = useState("");
  const [newQty, setNewQty] = useState("");
  const [newPack, setNewPack] = useState("");   // "" means leave the pack exactly as it is
  const [packOpts, setPackOpts] = useState({ pack_sizes: [], pack_unit: "" });
  const [dosesPerDay, setDosesPerDay] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (keepId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/prescription-reviews?state=${tab}`);
      const list = data?.reviews || [];
      setRows(list);
      setMeta({
        pending_count: data?.pending_count ?? 0,
        sla_hours: data?.sla_hours ?? 24,
        pay_window_hours: data?.pay_window_hours ?? 72,
      });
      // Keep the technician on the file they were reading instead of bouncing them to the queue.
      if (keepId) setSelected(list.find((r) => r.estimate_id === keepId) || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openFile = (r) => {
    setSelected(r);
    setMode(null);
    setNote("");
    setNewQty("");
    setNewPack("");
  };

  /* The pack sizes the pharmacy actually stocks for this medication. Loaded only when a file is
     open, because it is a catalogue lookup the queue itself has no use for. A failure here is not
     fatal: the technician simply gets no pack dropdown and can still change the quantity. */
  useEffect(() => {
    let alive = true;
    const id = selected?.estimate_id;
    if (!id) { setPackOpts({ pack_sizes: [], pack_unit: "" }); return undefined; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/prescription-reviews/${id}/pack-options`);
        if (alive) setPackOpts({ pack_sizes: data?.pack_sizes || [], pack_unit: data?.pack_unit || "" });
      } catch (e) {
        if (alive) setPackOpts({ pack_sizes: [], pack_unit: "" });
      }
    })();
    return () => { alive = false; };
  }, [selected?.estimate_id]);

  const copyLink = (token) => {
    const url = `${PUBLIC_ORIGIN}/estimate/${token}`;
    navigator.clipboard?.writeText(url);
    toast.success("Customer link copied");
  };

  const rr = selected?.rx_review || {};
  const cq = selected?.customer_quote || {};
  /* Quantity now counts PACKS. The pack itself lives in pack_size ("120 Tablets"), so every
     number the technician reads has to be shown against it. Estimates taken before the split
     carry no pack_size and fall back to the bare number they have always shown. */
  const selPack = String(selected?.pack_size || "").trim();
  const origPack = String(rr.requested_pack_size || selected?.original_pack_size || selPack).trim();
  const qLabel = (n) => packsLabel(n, selPack);
  const curQty = qtyInt(selected?.quantity, 1);
  const reqQty = qtyInt(rr.requested_quantity || selected?.original_quantity || selected?.quantity, curQty);

  /* Everything the technician changes is judged in TOTAL UNITS, because that is the only figure the
     customer actually pays for. 3 x 120 and 1 x 360 are the same 360 doses at the same money, so
     letting the pack change costs nothing as long as the total never rises above what was quoted. */
  const unitsOf = (q, p) => (packNum(p) ? q * packNum(p) : q);
  const effPack = newPack ? packLabel(parseInt(newPack, 10), selPack || packOpts.pack_unit) : selPack;
  const effLabel = (n) => packsLabel(n, effPack);
  const parsedQty = parseInt(newQty, 10);
  const origUnits = unitsOf(reqQty, origPack);
  const curUnits = unitsOf(curQty, selPack);
  const newUnits = Number.isFinite(parsedQty) && parsedQty > 0 ? unitsOf(parsedQty, effPack) : 0;
  const origLabel = packsLabel(reqQty, origPack);
  const perUnitMed = (Number(cq.medication) || 0) / Math.max(1, curUnits);
  const previewMed = newUnits > 0 ? perUnitMed * newUnits : 0;
  const previewTotal = previewMed + (Number(cq.shipping) || 0);
  const decided = rr.state && rr.state !== "pending";

  /* Mirrors the backend's rules so the technician sees the problem before a round trip.
     The backend stays the authority - anything it rejects still surfaces as a toast. */
  const adjustError = (() => {
    if (!Number.isFinite(parsedQty) || parsedQty < 1) return "Enter the quantity the prescription allows (1 or more).";
    if (newUnits > origUnits) return `You can only reduce. The customer was quoted ${origLabel}, so ${effLabel(parsedQty)} would charge them more than they agreed to.`;
    if (parsedQty === curQty && newUnits === curUnits) return `This is already set to ${qLabel(curQty)}. Approve it as it stands, or change the quantity or the pack size.`;
    if (!note.trim()) return "Add a short explanation. It is emailed to the customer.";
    return null;
  })();

  const submit = async () => {
    if (!selected) return;
    const id = selected.estimate_id;
    setSubmitting(true);
    try {
      if (mode === "approve") {
        const { data } = await api.post(`/admin/prescription-reviews/${id}/approve`, { note: note.trim() });
        toast.success(`Approved for ${qLabel(qtyInt(data?.quantity, curQty))}. Customer emailed, payment unlocked.`);
      } else if (mode === "adjust") {
        const { data } = await api.post(`/admin/prescription-reviews/${id}/adjust`, {
          quantity: parsedQty,
          note: note.trim(),
          pack_size: newPack ? parseInt(newPack, 10) : null,
        });
        toast.success(`Set to ${packsLabel(qtyInt(data?.new_quantity, parsedQty), data?.pack_size || effPack)}. New total ${money(data?.total)}, customer emailed.`);
      } else if (mode === "reject") {
        await api.post(`/admin/prescription-reviews/${id}/reject`, { reason: note.trim() });
        toast.success("Rejected. The customer has been asked to upload a new file.");
      }
      setMode(null);
      setNote("");
      setNewQty("");
      setNewPack("");
      await load(id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const fileUrl = selected ? `${BACKEND}/api/admin/prescription-reviews/${selected.estimate_id}/file` : null;
  const addr = selected?.shipping_address || {};
  const addrLine = [addr.street, addr.apt, addr.city, addr.state_province, addr.postal_code, addr.country].filter(Boolean).join(", ");

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="admin-prescription-review">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Pharmacy technician</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-paw-primary" strokeWidth={1.5} /> Prescription Review
          </h1>
          <p className="font-manrope text-sm text-paw-text-secondary dark:text-white/60 mt-2 max-w-2xl">
            Every uploaded prescription waits here before the customer can pay. Approve it, reduce the quantity to what the
            prescription actually allows, or reject an unusable file. We promise the customer a decision within {meta.sla_hours} hours,
            and they then get {meta.pay_window_hours} hours to pay.
          </p>
        </div>
        <button onClick={() => load(selected?.estimate_id)} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm transition-colors" data-testid="rx-review-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setSelected(null); setMode(null); }}
            className={`px-4 h-9 rounded-lg font-manrope text-sm border transition-colors ${tab === t.key
              ? "bg-paw-primary text-white border-paw-primary"
              : "bg-black/[0.03] dark:bg-white/5 text-paw-text-secondary dark:text-white/60 border-paw-border dark:border-white/10 hover:bg-black/[0.06] dark:hover:bg-white/10"}`}
            data-testid={`rx-tab-${t.key}`}>
            {t.label}
            {t.key === "pending" && meta.pending_count > 0 ? (
              <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-[11px] font-bold text-amber-950" data-testid="rx-pending-count">{meta.pending_count}</span>
            ) : null}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm" data-testid="rx-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-12 text-center" data-testid="rx-empty">
          <ShieldCheck className="w-8 h-8 mx-auto mb-3 text-paw-text-muted dark:text-white/30" strokeWidth={1.5} />
          <p className="font-manrope text-paw-text-muted dark:text-white/40">
            {tab === "pending" ? "Nothing is waiting for review. The queue is clear." : "No prescription reviews to show."}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl overflow-x-auto" data-testid="rx-queue">
          <table className="w-full">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-paw-border dark:border-white/5">
              <tr className="text-left">
                {["Customer", "Medication", "Requested", "Uploaded", "Decision due", "State", ""].map((h, i) => (
                  <th key={i} className={`px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium ${i === 6 ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.estimate_id} className="border-b border-paw-border dark:border-white/5 last:border-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors" data-testid={`rx-row-${r.estimate_id}`}>
                  <td className="px-5 py-4">
                    <p className="font-manrope text-sm font-medium">{r.full_name || "n/a"}</p>
                    <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">{r.email}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-manrope text-sm">{r.medication_name}</p>
                    <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">{[r.strength, r.dosage_form].filter(Boolean).join(" · ")}</p>
                  </td>
                  <td className="px-5 py-4 font-manrope text-sm">
                    {packsLabel(qtyInt(r.rx_review?.requested_quantity || r.original_quantity || r.quantity, 1), r.pack_size)}
                    {qtyInt(r.quantity, 1) !== qtyInt(r.rx_review?.requested_quantity || r.original_quantity || r.quantity, 1) ? (
                      <span className="text-paw-text-muted dark:text-white/40"> → {packsLabel(qtyInt(r.quantity, 1), r.pack_size)}</span>
                    ) : null}
                  </td>
                  <td className="px-5 py-4 font-manrope text-xs text-paw-text-secondary dark:text-white/60">{fmtWhen(r.rx_review?.submitted_at)}</td>
                  <td className="px-5 py-4 font-manrope text-xs">
                    {r.rx_review?.state === "pending" ? (
                      <span className={`inline-flex items-center gap-1 ${r.overdue ? "text-red-700 dark:text-red-300 font-semibold" : "text-paw-text-secondary dark:text-white/60"}`} data-testid={`rx-due-${r.estimate_id}`}>
                        {r.overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {countdown(r.rx_review?.due_at)}
                      </span>
                    ) : (
                      <span className="text-paw-text-muted dark:text-white/40">decided {fmtWhen(r.rx_review?.reviewed_at)}</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-manrope font-medium ${STATE_BADGE[r.rx_review?.state] || STATE_BADGE.pending}`}>
                      {STATE_LABEL[r.rx_review?.state] || r.rx_review?.state || "n/a"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => openFile(r)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-md text-xs font-manrope font-semibold transition-colors" data-testid={`rx-open-${r.estimate_id}`}>
                      <FileText className="w-3 h-3" /> {r.rx_review?.state === "pending" ? "Review" : "Open"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center px-3 py-6 overflow-y-auto" onClick={() => { setSelected(null); setMode(null); }} data-testid="rx-detail-modal">
          <div className="bg-paw-bg dark:bg-[#13161f] border border-paw-border dark:border-white/10 rounded-2xl max-w-6xl w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
              <div>
                <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-1">Attempt {rr.attempt || 1} · {selected.estimate_id}</p>
                <h2 className="font-outfit font-bold text-xl sm:text-2xl">{selected.full_name || selected.email}</h2>
                <span className={`inline-flex items-center px-2.5 py-1 mt-2 rounded-md border text-xs font-manrope font-medium ${STATE_BADGE[rr.state] || STATE_BADGE.pending}`} data-testid="rx-detail-state">
                  {STATE_LABEL[rr.state] || rr.state || "n/a"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {selected.link_token && (
                  <button onClick={() => copyLink(selected.link_token)} className="inline-flex items-center gap-1.5 px-3 h-9 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 rounded-lg font-manrope text-sm transition-colors" data-testid="rx-copy-link">
                    <Copy className="w-3.5 h-3.5" /> Customer link
                  </button>
                )}
                <button onClick={() => { setSelected(null); setMode(null); }} className="px-4 h-9 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 rounded-lg font-manrope text-sm transition-colors" data-testid="rx-close">Close</button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left: the prescription itself */}
              <div className="space-y-4">
                <Card title="Uploaded prescription" icon={FileText} testid="rx-viewer">
                  {!selected.has_prescription ? (
                    <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 text-center py-8">No prescription file on this estimate.</p>
                  ) : selected.prescription_kind === "pdf" ? (
                    <a href={fileUrl} target="_blank" rel="noreferrer" className="block bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 rounded-xl py-10 text-center font-manrope text-sm hover:bg-black/[0.06] dark:hover:bg-white/10 transition-colors" data-testid="rx-pdf-link">
                      Open PDF prescription ({selected.prescription_filename || "file.pdf"})
                    </a>
                  ) : (
                    <>
                      <img src={fileUrl} alt="Uploaded prescription" className="rounded-xl border border-paw-border dark:border-white/10 max-h-[560px] w-auto mx-auto bg-white" data-testid="rx-image" />
                      <a href={fileUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 font-manrope text-xs text-paw-primary hover:underline" data-testid="rx-open-full">
                        <FileText className="w-3 h-3" /> Open full size
                      </a>
                    </>
                  )}
                  <div className="mt-4 space-y-0.5">
                    <Row label="File name" value={selected.prescription_filename} testid="rx-filename" />
                    <Row label="Uploaded" value={fmtWhen(rr.submitted_at)} />
                    <Row label="Attempt" value={String(rr.attempt || 1)} />
                  </div>
                </Card>

                <Card title="Automated clarity check" icon={ShieldCheck} testid="rx-ai-scan">
                  <p className="font-manrope text-sm text-paw-text dark:text-white/85">
                    {selected.ai_scan?.passed ? "Legible" : "Flagged"}{selected.ai_scan?.reason ? `: ${selected.ai_scan.reason}` : ""}
                  </p>
                  <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-2">
                    This is a readability check only. It does not verify the authorised quantity. That is your call.
                  </p>
                </Card>
              </div>

              {/* Right: the whole customer file + the decision */}
              <div className="space-y-4">
                <Card title="What was requested" icon={ClipboardCheck} testid="rx-request">
                  <div className="space-y-0.5">
                    <Row label="Medication" value={selected.medication_name} testid="rx-med" />
                    <Row label="Strength / form" value={[selected.strength, selected.dosage_form].filter(Boolean).join(" · ")} />
                    <Row label="Quantity requested" value={origLabel} testid="rx-req-qty" />
                    <Row label="Total in those packs" value={packNum(origPack) ? `${origUnits} ${String(origPack).replace(/^[0-9\s.,]+/, "").trim() || "units"}` : null} testid="rx-req-units" />
                    {(curQty !== reqQty || curUnits !== origUnits) && <Row label="Now set to" value={`${qLabel(curQty)}${packNum(selPack) ? ` (${curUnits} in total)` : ""}`} testid="rx-cur-qty" />}
                    <Row label="Customer pays" value={`${money(cq.total ?? selected.quote)} (meds ${money(cq.medication)} + shipping ${money(cq.shipping)})`} testid="rx-total" />
                    <Row label="Priced by hand" value={selected.is_custom_med ? "Yes, custom medication" : null} />
                    <Row label="Link expires" value={`${fmtWhen(selected.link_expires_at)} (${countdown(selected.link_expires_at)})`} testid="rx-link-expiry" />
                  </div>
                </Card>

                <SupplyCalculator
                  dosesPerDay={dosesPerDay}
                  setDosesPerDay={setDosesPerDay}
                  unitsRequested={origUnits}
                  packUnits={packNum(effPack) || packNum(origPack) || 0}
                  unitLabel={String(origPack).replace(/^[0-9\s.,]+/, "").trim().toLowerCase() || "units"}
                  onUseQty={(n) => { setMode("adjust"); setNewQty(String(n)); }}
                />

                <Card title="Customer" testid="rx-customer">
                  <div className="space-y-0.5">
                    <Row label="Name" value={selected.full_name} />
                    <Row label="Email" value={selected.email} testid="rx-email" />
                    <Row label="Phone" value={selected.phone} />
                    <Row label="Date of birth" value={selected.customer_dob} testid="rx-dob" />
                    <Row label="Ships to" value={addrLine || null} testid="rx-address" />
                    <Row label="Client type" value={selected.client_type === "insurance" ? `Insurance: ${selected.insurance_partner_name || "partner"}` : selected.client_type} />
                    <Row label="Came from" value={selected.source} />
                  </div>
                </Card>

                <Card title="Pet" testid="rx-pet">
                  <div className="space-y-0.5">
                    <Row label="Name" value={selected.pet_name} />
                    <Row label="Type" value={selected.pet_type} />
                    <Row label="Date of birth" value={selected.pet_dob} />
                    <Row label="Other meds / allergies" value={selected.pet_meds_answer === "yes" ? (selected.pet_medication_info || "Yes, none listed") : selected.pet_meds_answer === "no" ? "None reported" : null} testid="rx-pet-meds" />
                  </div>
                </Card>

                {decided ? (
                  <Card title="Decision on file" icon={CheckCircle2} testid="rx-decision">
                    <div className="space-y-0.5">
                      <Row label="Outcome" value={STATE_LABEL[rr.state] || rr.state} />
                      <Row label="Quantity allowed" value={rr.approved_quantity ? packsLabel(qtyInt(rr.approved_quantity, 1), rr.approved_pack_size || selPack) : null} />
                      <Row label="Total dispensed" value={rr.approved_units ? String(rr.approved_units) : null} testid="rx-approved-units" />
                      <Row label="Reviewed" value={fmtWhen(rr.reviewed_at)} />
                      <Row label="Reviewed by" value={rr.reviewed_by} />
                      <Row label="Pay by" value={rr.pay_deadline ? fmtWhen(rr.pay_deadline) : null} />
                    </div>
                    {rr.pt_note && (
                      <p className="font-manrope text-sm text-paw-text dark:text-white/85 mt-3 bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 rounded-lg px-3 py-2 whitespace-pre-wrap" data-testid="rx-pt-note">{rr.pt_note}</p>
                    )}
                    <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-3">
                      {selected.status === "paid"
                        ? "This order has been paid. Nothing further is needed."
                        : "You can still change this decision below. The customer is emailed again each time."}
                    </p>
                  </Card>
                ) : null}

                {selected.status === "paid" ? (
                  <div className="bg-sky-500/10 border border-sky-500/25 rounded-2xl p-5" data-testid="rx-paid-notice">
                    <p className="font-manrope text-sm text-sky-800 dark:text-sky-200">This order is already paid, so there is nothing left to review.</p>
                  </div>
                ) : (
                  <Card title="Your decision" icon={Pencil} testid="rx-actions">
                    {!mode ? (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <button onClick={() => { setMode("approve"); setNote(""); }} className="inline-flex items-center justify-center gap-1.5 h-11 rounded-lg bg-paw-success hover:brightness-95 text-white font-manrope text-sm font-semibold transition-all" data-testid="rx-btn-approve">
                          <CheckCircle2 className="w-4 h-4" /> Approve {qLabel(reqQty)}
                        </button>
                        <button onClick={() => { setMode("adjust"); setNote(""); setNewQty(String(Math.max(1, curQty - 1))); }} className="inline-flex items-center justify-center gap-1.5 h-11 rounded-lg bg-paw-primary hover:bg-paw-primary-hover text-white font-manrope text-sm font-semibold transition-colors" data-testid="rx-btn-adjust">
                          <Pencil className="w-4 h-4" /> Adjust quantity
                        </button>
                        <button onClick={() => { setMode("reject"); setNote(""); }} className="inline-flex items-center justify-center gap-1.5 h-11 rounded-lg bg-paw-error hover:brightness-95 text-white font-manrope text-sm font-semibold transition-all" data-testid="rx-btn-reject">
                          <XCircle className="w-4 h-4" /> Reject file
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4" data-testid={`rx-form-${mode}`}>
                        <div className="bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 rounded-lg px-3 py-2">
                          <p className="font-manrope text-xs text-paw-text-secondary dark:text-white/70">
                            {mode === "approve" && `The prescription authorises the ${qLabel(reqQty)} the customer asked for. Payment unlocks and they get ${meta.pay_window_hours} hours to pay.`}
                            {mode === "adjust" && "Set this to what the prescription actually allows. You can change how many, and the pack size we dispense from, as long as the total does not go above what the customer was quoted. The order is re-priced, payment unlocks, and the customer does not upload anything again."}
                            {mode === "reject" && "Use this when the file is unusable, for example a wrong document, an unreadable photo, or something that is not a prescription at all. Payment stays locked and the customer is asked to upload again."}
                          </p>
                        </div>

                        {mode === "adjust" && (
                          <div>
                            <div className={`grid grid-cols-1 gap-3 ${packOpts.pack_sizes.length > 0 ? "sm:grid-cols-2" : ""}`}>
                              <div>
                                <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/50 block mb-1.5" htmlFor="rx-qty-input">{selPack ? "How many" : "Quantity the prescription allows"}</label>
                                <input id="rx-qty-input" type="number" min="1" step="1" value={newQty}
                                  onChange={(e) => setNewQty(e.target.value)}
                                  className="w-full h-11 px-4 rounded-xl bg-white dark:bg-white/5 border border-paw-border dark:border-white/10 text-paw-text dark:text-white text-sm font-manrope focus:outline-none focus:border-paw-primary"
                                  data-testid="rx-adjust-qty" />
                              </div>
                              {packOpts.pack_sizes.length > 0 && (
                                <div>
                                  <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/50 block mb-1.5" htmlFor="rx-pack-input">Pack size we dispense</label>
                                  <select id="rx-pack-input" value={newPack} onChange={(e) => setNewPack(e.target.value)}
                                    className="w-full h-11 px-4 rounded-xl bg-white dark:bg-white/5 border border-paw-border dark:border-white/10 text-paw-text dark:text-white text-sm font-manrope focus:outline-none focus:border-paw-primary"
                                    data-testid="rx-adjust-pack">
                                    <option value="">{selPack ? `Leave as ${selPack}` : "Leave as it is"}</option>
                                    {packOpts.pack_sizes.map((p) => (
                                      <option key={p} value={String(p)}>{packLabel(p, selPack || packOpts.pack_unit)}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-2" data-testid="rx-units-hint">
                              A prescription says how much medication, not which box we send it in. Set the pack to what you are actually dispensing, and the quantity to how many of those. The customer was quoted {origLabel}{packNum(origPack) ? `, which is ${origUnits} in total` : ""}. You can go down to any amount at or below that.
                            </p>
                            {newUnits > 0 && newUnits <= origUnits && (
                              <div className="mt-3 bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 rounded-lg px-4 py-3 font-manrope text-sm space-y-1" data-testid="rx-price-preview">
                                <div className="flex justify-between text-paw-text-secondary dark:text-white/60"><span>Dispensing</span><span data-testid="rx-preview-units">{effLabel(parsedQty)}{packNum(effPack) ? ` (${newUnits} in total)` : ""}</span></div>
                                <div className="flex justify-between text-paw-text-secondary dark:text-white/60"><span>Medication ({money(perUnitMed)} each)</span><span>{money(previewMed)}</span></div>
                                <div className="flex justify-between text-paw-text-secondary dark:text-white/60"><span>Shipping</span><span>{money(cq.shipping)}</span></div>
                                <div className="flex justify-between pt-2 border-t border-paw-border dark:border-white/10 text-base"><span className="font-semibold">New customer total</span><span className="font-bold text-paw-primary" data-testid="rx-preview-total">{money(previewTotal)}</span></div>
                                <p className="text-xs text-paw-text-muted dark:text-white/40 pt-1">Estimated. The pricing engine recalculates the exact total when you submit, and the email quotes that figure.</p>
                              </div>
                            )}
                          </div>
                        )}

                        <div>
                          <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/50 block mb-1.5" htmlFor="rx-note-input">
                            {mode === "approve" ? "Note for the customer (optional)" : mode === "adjust" ? "Explain the change (emailed to the customer)" : "What was wrong with the file (emailed to the customer)"}
                          </label>
                          <textarea id="rx-note-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                            placeholder={mode === "adjust" ? "e.g. The prescription authorises one inhaler with no refills, so we can only dispense 1 of the 3 requested." : mode === "reject" ? "e.g. The photo shows an invoice rather than the prescription. Please upload the signed prescription from your vet." : "Anything the customer should know."}
                            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-paw-border dark:border-white/10 text-paw-text dark:text-white text-sm font-manrope focus:outline-none focus:border-paw-primary resize-y"
                            data-testid="rx-note" />
                        </div>

                        {mode === "adjust" && adjustError && <p className="font-manrope text-xs text-red-700 dark:text-red-300" data-testid="rx-adjust-error">{adjustError}</p>}
                        {mode === "reject" && !note.trim() && <p className="font-manrope text-xs text-red-700 dark:text-red-300" data-testid="rx-reject-error">Tell the customer what to send instead. This text goes in the email.</p>}

                        <div className="flex gap-2">
                          <button onClick={submit}
                            disabled={submitting || (mode === "adjust" && !!adjustError) || (mode === "reject" && !note.trim())}
                            className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-paw-primary hover:bg-paw-primary-hover text-white disabled:opacity-50 disabled:cursor-not-allowed font-manrope text-sm font-semibold transition-colors"
                            data-testid="rx-submit">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {mode === "approve" ? `Approve and unlock payment` : mode === "adjust" ? `Set to ${Number.isFinite(parsedQty) && parsedQty > 0 ? effLabel(parsedQty) : "…"} and email` : "Reject and ask for a new upload"}
                          </button>
                          <button onClick={() => { setMode(null); setNote(""); setNewQty(""); setNewPack(""); }} className="px-4 h-11 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 rounded-lg font-manrope text-sm transition-colors" data-testid="rx-cancel">Cancel</button>
                        </div>
                      </div>
                    )}
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
